import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Divider, 
  LinearProgress, 
  Alert,
  TablePagination,
} from '@mui/material';
import Table from '../../../Table/Table';
import apiClient from '../../../../pages/keycloak/axiosInstance';

const tableHeaders = [
  { displayName: 'S.No', dbParam: 'serialNo' },
  { displayName: 'Flash Date', dbParam: 'Flash Date' },
  { displayName: 'Inspector Name', dbParam: 'inspectorName' },
  { displayName: 'Module Serial Nos', dbParam: 'Module_No' },
  { displayName: 'Pmax', dbParam: 'Pmax' },
  { displayName: 'Voc', dbParam: 'Voc' },
  { displayName: 'Isc', dbParam: 'Isc' },
  { displayName: 'Vmp', dbParam: 'Vmpp' },
  { displayName: 'Imp', dbParam: 'Imp' },
  { displayName: 'FF', dbParam: 'FF' },
  { displayName: 'Difference Pmax (%)', dbParam: 'PM_diff' },
];

const StepOne = ({ onFileUploadStatus, onComplete, onToleranceChange }) => {
  const [file, setFile] = useState(null);
  const [plusTolerance, setPlusTolerance] = useState('');
  const [minusTolerance, setMinusTolerance] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // ── New states for error handling ──
  const [apiError, setApiError] = useState(null);

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const dataFromSession = sessionStorage.getItem('witnessTableData');
    if (dataFromSession) {
      const storedData = JSON.parse(dataFromSession);
      processTableData(storedData);
    }
  }, []);

  const handleFileUpload = async (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    onFileUploadStatus?.(true);

    // Reset previous error
    setApiError(null);

    setLoading(true);
    setUploadProgress(0);
    setTableLoading(true);

    const projectId = localStorage.getItem('selectedProject');
    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('projectId', projectId);

    try {
      const response = await apiClient.post(`get_pmax_diff`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      // Check if response contains error field (your backend pattern)
      if (response.data?.error) {
        setApiError(response.data.error);
        setTableData([]); // Clear table on error
      } else {
        processTableData(response.data);
        const fileName = response.data[0]?.fileName || uploadedFile.name;
        sessionStorage.setItem('uploadedFile', fileName);
        // sessionStorage.setItem('witnessTableData', JSON.stringify(response.data));
      }
    } catch (error) {
      // Network / server errors
      const errorMessage = 
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        'Failed to process file. Please try again.';
      
      setApiError(errorMessage);
      setTableData([]);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const processTableData = (data) => {
    const inspectorName = sessionStorage.getItem('username') || 'Inspector';
    const processedData = data.map((item, index) => ({
      ...item,
      serialNo: index + 1,
      inspectorName: inspectorName,
    }));
    setTableData(processedData);
    setPage(0);  // ← reset to first page on new upload
  };

  const handlePlusToleranceChange = (e) => {
    const value = e.target.value;
    if (value === '' || parseFloat(value) >= 0) {
      setPlusTolerance(value);
      onToleranceChange(value, minusTolerance);
    }
  };

  const handleMinusToleranceChange = (e) => {
    const value = e.target.value;
    if (value === '' || parseFloat(value) <= 0) {
      setMinusTolerance(value);
      onToleranceChange(plusTolerance, value);
    }
  };

  const isRowHighlighted = (PM_diff) => {
    const plusTol = parseFloat(plusTolerance);
    const minusTol = parseFloat(minusTolerance);
    return (PM_diff > plusTol) || (PM_diff < minusTol);
  };

  return (
    <Box sx={{ py: 2, px: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Step 1: Upload PDI Sample
      </Typography>

      {/* File Upload Section */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Upload your PDI Sample
        </Typography>
        <Button
          variant="contained"
          component="label"
          color="primary"
          sx={{
            mb: 2,
            backgroundColor: '#424752',
            '&:hover': { backgroundColor: '#6c757d' },
          }}
        >
          Upload File
          <input hidden type="file" onChange={handleFileUpload} accept=".csv,.xlsx,.xls" />
        </Button>
        {file && <Typography variant="body2" sx={{ mt: 1 }}>Uploaded: {file.name}</Typography>}
      </Box>

      {/* Upload Progress */}
      {loading && (
        <Box sx={{ my: 2 }}>
          <Typography variant="body2">Uploading... {uploadProgress}%</Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
        </Box>
      )}

      {/* Error Message */}
      {apiError && (
        <Alert 
          severity="error" 
          sx={{ my: 2 }}
          onClose={() => setApiError(null)}
        >
          {apiError}
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Tolerance Inputs */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          label="Define Tolerance (+)"
          variant="outlined"
          type="number"
          inputProps={{ step: '0.01', min: "0" }}
          value={plusTolerance}
          onChange={handlePlusToleranceChange}
          fullWidth
        />
        <TextField
          label="Define Tolerance (-)"
          variant="outlined"
          type="number"
          inputProps={{ step: '0.01', max: "0" }}
          value={minusTolerance}
          onChange={handleMinusToleranceChange}
          fullWidth
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Table Section */}
      <Typography variant="h6" fontWeight="medium" gutterBottom>
        PDI Sample Data
      </Typography>

      {tableLoading ? (
        <Box sx={{ width: '100%', my: 2 }}>
          <Typography variant="body2">Loading table data...</Typography>
          <LinearProgress />
        </Box>
      ) : tableData.length === 0 && !apiError ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No data to display yet. Upload a file to begin.
        </Typography>
      ) : (
        <>
          <Table 
            tableHeaders={tableHeaders} 
            tableData={tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)} 
            rowHighlightCondition={(row) => isRowHighlighted(row.PM_diff)} 
          />
          <TablePagination
            component="div"
            count={tableData.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
};

export default StepOne;